import { useLanguage } from '@/contexts/LanguageContext';

export default function ContactInfo() {
  const { t } = useLanguage();

  const services = [
    'PQernel®', 'PQMB', 'rvPlastics',
    t('塑膠再生', 'Plastic Recycling', '廃プラスチック再生'),
    t('低碳材料', 'Low-Carbon Materials', '低炭素材料'),
    t('循環經濟解決方案', 'Circular Economy Solutions', 'サーキュラーエコノミーソリューション'),
  ];

  const targets = [
    t('塑膠回收廠', 'Plastic Recyclers', 'プラスチックリサイクル業者'),
    t('造粒廠', 'Compounders', '造粒業者'),
    t('製造商', 'Manufacturers', 'メーカー'),
    t('品牌端', 'Brand Owners', 'ブランドオーナー'),
    t('經銷商', 'Distributors', '販売代理店'),
    t('政府與 ESG 專案單位', 'Government & ESG Units', '政府・ESGプロジェクト機関'),
  ];

  const prepareCards = [
    {
      icon: 'ri-stack-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('現有原料類型', 'Raw Material Types', '原料種類'),
      desc: t(
        'rPET、rPE、rPP、ABS、PU、TPE 或混合塑料',
        'rPET, rPE, rPP, ABS, PU, TPE, or mixed plastics',
        'rPET・rPE・rPP・ABS・PU・TPEまたは混合プラスチック'
      ),
    },
    {
      icon: 'ri-settings-3-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('目前製程', 'Current Process', '現在の製造工程'),
      desc: t(
        '造粒、射出、押出、吹膜、包材或其他製程',
        'Compounding, injection, extrusion, film blowing, packaging, etc.',
        '造粒・射出・押出・フィルム成形・包装材など'
      ),
    },
    {
      icon: 'ri-box-3-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('目標應用', 'Target Application', '目標用途'),
      desc: t(
        '托盤、籃框、包材、農業資材、工業零件、民生用品等',
        'Trays, baskets, packaging, agricultural materials, industrial parts, etc.',
        'トレー・バスケット・包装材・農業資材・工業部品など'
      ),
    },
    {
      icon: 'ri-error-warning-line',
      iconColor: 'text-brand-green',
      iconBg: 'bg-brand-green-light',
      title: t('希望改善的問題', 'Issues to Improve', '改善したい課題'),
      desc: t(
        '物性不足、加工不穩、成本、再生料比例、ESG 或碳足跡需求',
        'Mechanical properties, processing stability, cost, recycled content, ESG or carbon footprint',
        '物性不足・加工不安定・コスト・再生材料比率・ESG要件'
      ),
    },
  ];

  return (
    <section className="section-padding bg-white">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">

          {/* Left: Company info card */}
          <div className="lg:col-span-2 bg-[#F5F8FB] rounded-2xl border border-neutral-border p-7 flex flex-col">
            {/* Logo row */}
            <div className="flex items-center gap-3 mb-6 pb-5 border-b border-neutral-border">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-brand-green to-brand-blue flex-shrink-0">
                <i className="ri-leaf-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="font-bold text-neutral-dark text-[1.0625rem] leading-tight">
                  {t('聚量應用材料', 'PolyQ Applied Materials', '聚量応用材料')}
                </p>
                <p className="text-neutral-mid text-[0.8125rem]">PolyQ Applied Materials Co., Ltd.</p>
              </div>
            </div>

            <p className="text-neutral-mid text-[0.9375rem] leading-relaxed mb-6">
              {t(
                '歡迎與我們討論材料技術、產品導入、經銷合作與 ESG 循環經濟專案。',
                'We welcome discussions on material technology, product implementation, distribution partnerships, and ESG circular economy projects.',
                '材料技術・製品導入・販売代理・ESGサーキュラーエコノミープロジェクトについてお気軽にご相談ください。'
              )}
            </p>

            {/* Contact quick links */}
            <div className="space-y-3 mb-6">
              <a
                href="tel:+886958471138"
                className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-neutral-border hover:border-brand-green/40 hover:bg-brand-green-light/30 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0">
                  <i className="ri-phone-fill text-brand-green text-base"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[0.75rem] text-neutral-mid font-medium">{t('聯絡電話', 'Phone', '電話番号')}</p>
                  <p className="text-neutral-dark text-[0.9375rem] font-semibold group-hover:text-brand-green transition-colors">+886 958-471-138</p>
                </div>
              </a>
              <a
                href="mailto:chang.chunghung@polyquantummaterials.com"
                className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-neutral-border hover:border-brand-green/40 hover:bg-brand-green-light/30 transition-all duration-200 cursor-pointer group"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0">
                  <i className="ri-mail-fill text-brand-green text-base"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[0.75rem] text-neutral-mid font-medium">Email</p>
                  <p className="text-neutral-dark text-[0.875rem] font-semibold group-hover:text-brand-green transition-colors break-all leading-snug">
                    chang.chunghung@polyquantummaterials.com
                  </p>
                </div>
              </a>
              <div className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-neutral-border">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0">
                  <i className="ri-map-pin-2-fill text-brand-green text-base"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[0.75rem] text-neutral-mid font-medium">{t('所在地', 'Location', '所在地')}</p>
                  <p className="text-neutral-dark text-[0.875rem] font-semibold leading-snug">
                    {t('429台中市神岡區大富路12號', '12, Dafu Rd., Shengang Dist., Taichung City 429', '台湾台中市神岡区大富路12号 429')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3.5 rounded-xl bg-white border border-neutral-border">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0">
                  <i className="ri-time-fill text-brand-green text-base"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-[0.75rem] text-neutral-mid font-medium">{t('回覆時間', 'Response Time', '返信時間')}</p>
                  <p className="text-neutral-dark text-[0.9375rem] font-semibold">
                    {t('3 個工作天內', 'Within 3 business days', '3営業日以内')}
                  </p>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="mb-4">
              <p className="text-[0.75rem] font-semibold text-neutral-mid uppercase tracking-wider mb-2">
                {t('服務項目', 'Services', 'サービス内容')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {services.map((s) => (
                  <span key={s} className="inline-block px-2.5 py-0.5 rounded-full bg-brand-green-light text-brand-green text-[0.8125rem] font-medium">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <p className="text-[0.75rem] font-semibold text-neutral-mid uppercase tracking-wider mb-2">
                {t('服務對象', 'Target Clients', '対象クライアント')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {targets.map((tgt) => (
                  <span key={tgt} className="inline-block px-2.5 py-0.5 rounded-full bg-[#EAF2FB] text-[#2980B9] text-[0.8125rem] font-medium">
                    {tgt}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Prepare cards */}
          <div className="lg:col-span-3">
            <p className="text-[0.9375rem] font-semibold text-brand-green uppercase tracking-widest mb-2">
              {t('聯絡前準備', 'Before You Contact', 'お問い合わせ前の準備')}
            </p>
            <h2
              className="font-bold text-neutral-dark mb-6"
              style={{ fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)', lineHeight: '1.4' }}
            >
              {t(
                '為了更快協助您評估，建議先準備以下資訊',
                'To help us evaluate faster, please prepare the following information',
                'より迅速な評価のために、以下の情報をご準備ください'
              )}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {prepareCards.map((card) => (
                <div
                  key={card.title}
                  className="flex items-start gap-4 p-5 bg-[#F5F8FB] rounded-xl border border-neutral-border hover:border-brand-green/30 transition-all duration-200"
                >
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${card.iconBg} flex-shrink-0`}>
                    <i className={`${card.icon} ${card.iconColor} text-xl`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-dark text-[1rem] mb-1">{card.title}</p>
                    <p className="text-neutral-mid text-[0.9375rem] leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Process steps */}
            <div className="bg-gradient-to-br from-brand-green/8 to-brand-blue/5 rounded-2xl border border-brand-green/20 p-6">
              <p className="font-semibold text-neutral-dark text-[1rem] mb-4">
                {t('合作流程', 'Cooperation Process', '協力プロセス')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                {[
                  { step: '01', label: t('送出諮詢', 'Submit Inquiry', 'お問い合わせ') },
                  { step: '02', label: t('需求確認', 'Needs Assessment', 'ニーズ確認') },
                  { step: '03', label: t('試產驗證', 'Pilot Production', '試験生産') },
                  { step: '04', label: t('正式導入', 'Full Implementation', '本格導入') },
                ].map((item, idx, arr) => (
                  <div key={item.step} className="flex sm:flex-col items-center sm:items-start gap-2 sm:gap-1">
                    <div className="flex items-center gap-2 sm:gap-0">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-green text-white text-[0.8125rem] font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      {idx < arr.length - 1 && (
                        <div className="hidden sm:block w-full h-px bg-brand-green/30 mt-0 mx-1 flex-1" />
                      )}
                    </div>
                    <p className="text-neutral-dark text-[0.875rem] font-medium sm:mt-2">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
